import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNav } from "../../components/NavContext";
import { daysOfWeek } from "../Utils/Constants";

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const HOUR_OPTIONS = Array.from({ length: 48 }, (_, i) => {
    const h = Math.floor(i / 2);
    const m = i % 2 === 0 ? "00" : "30";
    return `${String(h).padStart(2, "0")}:${m}`;
});

export default function CreateDoctor() {
    const API_URL = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();
    const DOMAIN = process.env.REACT_APP_DOMAIN;
    axios.defaults.withCredentials = true;
    const usernameWasAutoGenerated = useRef(false);
    const [formData, setFormData] = useState({
        // Personal
        full_name: "",
        username: "",
        password: "slj20982n4S3",
        gender: "",
        // Professional
        field_id: "",
        brief: "",
        info: "",
        license_number: "",
        license_date: "",
        // Media
        profile_picture: null,
        // Clinic
        clinic_address: "",
        clinic_latitude: "",
        clinic_longitude: "",
        phone_number: "",
        open_days: [],
        open_day_times: {},
        // Availability & Fees
        is_phone: false,
        is_clinic: false,
        consultation_fee: "",
        clinic_consultation_fee: "",
        // Social
        facebook_url: "",
        instagram_url: "",
    });

    const [clinicLocation, setClinicLocation] = useState(null);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [mapKey, setMapKey] = useState(0);
    const [locationLoading, setLocationLoading] = useState(true);

    const { addLink } = useNav();

    const makeUsernameFromArabicName = (name) => {
        const raw = (name || '').trim();
        if (!raw) return '';

        const map = {
            'ا': 'a', 'أ': 'a', 'إ': 'e', 'آ': 'a',
            'ب': 'b',
            'ت': 't', 'ث': 't',
            'ج': 'j',
            'ح': 'h', 'خ': 'k',
            'د': 'd', 'ذ': 'd',
            'ر': 'r', 'ز': 'z',
            'س': 's', 'ش': 's',
            'ص': 's', 'ض': 'd',
            'ط': 't', 'ظ': 'z',
            'ع': 'a', 'غ': 'g',
            'ف': 'f',
            'ق': 'q',
            'ك': 'k',
            'ل': 'l',
            'م': 'm',
            'ن': 'n',
            'ه': 'h',
            'و': 'w',
            'ي': 'y', 'ى': 'a',
            'ة': 'a',
            'ؤ': 'w', 'ئ': 'e',
        };

        const letters = [];
        for (const ch of raw) {
            if (map[ch]) {
                letters.push(map[ch]);
            } else if (/[a-zA-Z]/.test(ch)) {
                letters.push(ch.toLowerCase());
            }
        }

        let base = letters.join('').replace(/[^a-z]/g, '');
        if (!base) base = 'doctor';

        // Always exactly 7 letters
        if (base.length >= 7) return base.slice(0, 7);
        while (base.length < 7) base += 'a';
        return base;
    };

    const randomLetters = (len) => {
        const alphabet = 'abcdefghijklmnopqrstuvwxyz';
        let out = '';
        for (let i = 0; i < len; i++) {
            out += alphabet[Math.floor(Math.random() * alphabet.length)];
        }
        return out;
    };

    const findAvailableUsername = async (base7) => {
        const candidates = [];
        const safeBase = (base7 || '').toLowerCase().replace(/[^a-z]/g, '').slice(0, 7);
        if (safeBase.length === 7) candidates.push(safeBase);
        // Try variants while keeping 7 chars
        for (let i = 0; i < 4; i++) {
            const suffix = randomLetters(2);
            candidates.push((safeBase.slice(0, 5) + suffix).slice(0, 7));
        }
        for (let i = 0; i < 4; i++) {
            candidates.push(randomLetters(7));
        }

        for (const username of candidates) {
            try {
                const response = await axios.get(`${API_URL}admin/doctor/username-available?username=${encodeURIComponent(username)}`, {
                });
                if (response.data?.available) {
                    return username;
                }
            } catch (_) {
                // If availability check fails, fall back to candidate without blocking the form
                return username;
            }
        }

        return safeBase || randomLetters(7);
    };

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            const fullName = (formData.full_name || '').trim();
            if (!fullName) return;

            const currentUsername = (formData.username || '').trim();

            // Only auto-generate when username is empty OR previously auto-generated
            if (currentUsername && !usernameWasAutoGenerated.current) return;

            const base7 = makeUsernameFromArabicName(fullName);
            if (!base7) return;

            const available = await findAvailableUsername(base7);
            if (cancelled) return;

            setFormData((prev) => ({ ...prev, username: available }));
            usernameWasAutoGenerated.current = true;
        };

        run();
        return () => {
            cancelled = true;
        };
        // Intentionally only depend on full_name
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.full_name]);

    useEffect(() => {
      addLink("اطبائنا", `${DOMAIN}control/doctor-list`);
      addLink("اضافة طبيب", `${DOMAIN}control/doctor-create`);
      addLink("الحجوزات", `${DOMAIN}control/order-list`);
      addLink('online', `${DOMAIN}control/admin/online-notes`);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addLink]);    
    
    const [fields, setFields] = useState([]);
    const [message, setMessage] = useState({ type: "", text: "" });

    useEffect(() => {
        const fetchFields = async () => {
            try {
                const response = await axios.get(`${API_URL}fields/list`);
                setFields(response.data);
            } catch (err) {
                setMessage({ type: "error", text: "Failed to load fields information." });
            }
        };
        fetchFields();
    }, [API_URL]);

    useEffect(() => {
        if (!navigator.geolocation) {
            setLocationLoading(false);
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const loc = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                };
                setCurrentLocation(loc);
                setLocationLoading(false);
                setMapKey((prev) => prev + 1);
            },
            () => {
                setLocationLoading(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000,
            }
        );
    }, []);

    useEffect(() => {
        const latRaw = formData.clinic_latitude;
        const lngRaw = formData.clinic_longitude;

        if (latRaw === '' || lngRaw === '' || latRaw == null || lngRaw == null) return;

        const lat = Number(latRaw);
        const lng = Number(lngRaw);

        if (Number.isNaN(lat) || Number.isNaN(lng)) return;
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return;

        const differs =
            !clinicLocation ||
            Number(clinicLocation.latitude) !== lat ||
            Number(clinicLocation.longitude) !== lng;

        if (!differs) return;

        setClinicLocation({ latitude: lat, longitude: lng });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.clinic_latitude, formData.clinic_longitude]);

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        setFormData((prev) => {
            const next = { ...prev };

            if (name === "profile_picture") {
                next.profile_picture = files[0];
            } else if (name === 'username') {
                next.username = value;
                usernameWasAutoGenerated.current = false;
            } else if (name === "is_phone" || name === "is_clinic") {
                next[name] = checked;
            } else if (type === "checkbox") {
                // Toggle open day and manage corresponding times
                const updatedDays = checked
                    ? [...prev.open_days, name]
                    : prev.open_days.filter((d) => d !== name);
                const updatedTimes = { ...(prev.open_day_times || {}) };
                if (checked) {
                    if (!updatedTimes[name]) {
                        updatedTimes[name] = { opening_time: "", closing_time: "" };
                    }
                } else {
                    delete updatedTimes[name];
                }
                next.open_days = updatedDays;
                next.open_day_times = updatedTimes;
            } else {
                next[name] = value;
            }
            return next;
        });
    };

    const handleTimeChange = (day, field, value) => {
        setFormData((prev) => {
            const times = { ...(prev.open_day_times || {}) };
            if (!times[day]) {
                times[day] = { opening_time: "", closing_time: "" };
            }
            times[day] = { ...times[day], [field]: value };
            return { ...prev, open_day_times: times };
        });
    };

    // OpenStreetMap component with click handler
    const MapClickHandler = ({ onMapClick }) => {
        useMapEvents({
            click: (e) => {
                const { lat, lng } = e.latlng;
                onMapClick({ latitude: lat, longitude: lng });
            },
        });
        return null;
    };

    const OpenStreetMapComponent = ({ center, clinicLocation, onMapClick }) => {
        const mapCenter = clinicLocation 
            ? [clinicLocation.latitude, clinicLocation.longitude]
            : [center.lat, center.lng];

        return (
            <MapContainer
                center={mapCenter}
                zoom={13}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapClickHandler onMapClick={onMapClick} />
                {clinicLocation && (
                    <Marker 
                        position={[clinicLocation.latitude, clinicLocation.longitude]}
                        title="Clinic Location"
                    />
                )}
            </MapContainer>
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: "", text: "" });

        const formDataToSend = new FormData();
        const skipIfEmpty = new Set([
            'clinic_latitude',
            'clinic_longitude',
            'consultation_fee',
            'clinic_consultation_fee',
            'license_number',
            'license_date',
        ]);

        Object.keys(formData).forEach((key) => {
            const val = formData[key];

            if (key === 'profile_picture') {
                if (!val) return;
                formDataToSend.append(key, val);
                return;
            }

            if (key === 'open_days' && Array.isArray(val)) {
                if (val.length === 0) return;
                val.forEach((day) => formDataToSend.append('open_days[]', day));
                return;
            }

            if (key === 'open_day_times') {
                return;
            }

            if (key === 'is_clinic' || key === 'is_phone') {
                formDataToSend.append(key, val ? 1 : 0);
                return;
            }

            if (key === 'facebook_url' || key === 'instagram_url') {
                formDataToSend.append(key, (!val || val === 'null') ? '' : val);
                return;
            }

            if (skipIfEmpty.has(key) && (val === '' || val === undefined || val === null)) {
                return;
            }

            formDataToSend.append(key, val);
        });

        // Append open_day_times as nested fields similar to DoctorInformation.js
        if (formData.open_day_times && typeof formData.open_day_times === 'object') {
            formData.open_days.forEach((day) => {
                const times = formData.open_day_times[day];
                if (!times) return;
                const ot = times.opening_time || "";
                const ct = times.closing_time || "";
                if (ot !== "") {
                    formDataToSend.append(`open_day_times[${day}][opening_time]`, ot);
                }
                if (ct !== "") {
                    formDataToSend.append(`open_day_times[${day}][closing_time]`, ct);
                }
            });
        }

        try {
            await axios.post(`${API_URL}admin/doctor/create`, formDataToSend, {
                headers: { 
                    "Content-Type": "multipart/form-data",
                },
            });
            setMessage({ type: "success", text: "Doctor created successfully!" });
            navigate("/control/doctor-list"); // Redirect after creation
        } catch (err) {
            const apiMsg = err?.response?.data?.message || "Unknown error";
            const apiErrors = err?.response?.data?.errors;
            const details = apiErrors && typeof apiErrors === 'object'
                ? Object.values(apiErrors).flat().filter(Boolean).join(' | ')
                : '';
            setMessage({ type: "error", text: "Failed to create doctor: " + apiMsg + (details ? " - " + details : "") });
        }
    };

    return (
        <div className="container mt-4" dir="rtl">
            <div className="row justify-content-center">
                <div className="col-lg-10">
                    <header className="mb-4 text-center">
                        <h1 className="h3 mb-1">إضافة طبيب جديد</h1>
                        <div className="text-muted">إدخال بيانات الطبيب مرتبة حسب الأقسام</div>
                    </header>
                    {message.text && (
                        <div className={`alert ${message.type === "error" ? "alert-danger" : "alert-success"}`}>
                            {message.text}
                        </div>
                    )}
                    <form onSubmit={handleSubmit}>
                        {/* Personal & Account */}
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-light fw-semibold">المعلومات الشخصية والحساب</div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">الاسم الكامل</label>
                                        <input type="text" name="full_name" value={formData.full_name} onChange={handleChange} className="form-control" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">اسم المستخدم</label>
                                        <input type="text" name="username" value={formData.username || ""} dir="ltr" onChange={handleChange} className="form-control" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">كلمة المرور</label>
                                        <input type="password" name="password" value={formData.password} onChange={handleChange} className="form-control" />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">الجنس</label>
                                        <select name="gender" value={formData.gender} onChange={handleChange} className="form-select">
                                            <option value="">اختر الجنس</option>
                                            <option value="male">ذكر</option>
                                            <option value="female">أنثى</option>
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">التخصص</label>
                                        <select name="field_id" value={formData.field_id} onChange={handleChange} className="form-select">
                                            <option value="">اختر التخصص</option>
                                            {fields.map((field) => (
                                                <option key={field.field_id} value={field.field_id}>{field.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Professional */}
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-light fw-semibold">المعلومات المهنية</div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">نبذة</label>
                                        <textarea name="brief" value={formData.brief || ""} onChange={handleChange} className="form-control" rows={2} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">معلومات إضافية</label>
                                        <textarea name="info" value={formData.info || ""} onChange={handleChange} className="form-control" rows={2} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">رقم الرخصة</label>
                                        <input type="text" name="license_number" value={formData.license_number || ""} onChange={handleChange} className="form-control" />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">تاريخ الرخصة</label>
                                        <input type="date" name="license_date" value={formData.license_date || ""} onChange={handleChange} className="form-control" />
                                    </div>
                                    <div className="col-md-4 d-flex align-items-center justify-content-between">
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" id="is_phone" name="is_phone" checked={!!formData.is_phone} onChange={handleChange} />
                                            <label className="form-check-label" htmlFor="is_phone">تفعيل استشارة هاتف</label>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" id="is_clinic" name="is_clinic" checked={!!formData.is_clinic} onChange={handleChange} />
                                            <label className="form-check-label" htmlFor="is_clinic">تفعيل حجز العيادة</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Fees */}
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-light fw-semibold">الأسعار</div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">سعر الاستشارة أونلاين</label>
                                        <input type="text" name="consultation_fee" value={formData.consultation_fee || ""} dir="ltr" onChange={handleChange} className="form-control" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">سعر الاستشارة في العيادة</label>
                                        <input type="text" name="clinic_consultation_fee" value={formData.clinic_consultation_fee || ""} dir="ltr" onChange={handleChange} className="form-control" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Clinic */}
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-light fw-semibold">العيادة</div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-12">
                                        <label className="form-label">عنوان العيادة</label>
                                        <textarea name="clinic_address" value={formData.clinic_address || ""} onChange={handleChange} className="form-control" rows={2} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">رقم الهاتف</label>
                                        <input type="text" name="phone_number" value={formData.phone_number || ""} dir="ltr" onChange={handleChange} className="form-control" />
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <label className="form-label d-block mb-2">موقع العيادة على الخريطة</label>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary btn-sm"
                                                disabled={locationLoading}
                                                onClick={() => {
                                                    if (!navigator.geolocation) return;
                                                    setLocationLoading(true);
                                                    navigator.geolocation.getCurrentPosition(
                                                        (position) => {
                                                            const loc = {
                                                                latitude: position.coords.latitude,
                                                                longitude: position.coords.longitude,
                                                            };
                                                            setClinicLocation(loc);
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                clinic_latitude: String(loc.latitude),
                                                                clinic_longitude: String(loc.longitude),
                                                            }));
                                                            setLocationLoading(false);
                                                            setMapKey((prev) => prev + 1);
                                                        },
                                                        () => {
                                                            setLocationLoading(false);
                                                        },
                                                        {
                                                            enableHighAccuracy: true,
                                                            timeout: 10000,
                                                            maximumAge: 0,
                                                        }
                                                    );
                                                }}
                                            >
                                                {locationLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                        جاري التحديد...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-crosshairs me-1"></i>
                                                        استخدام الموقع الحالي
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                        <small className="text-muted text-end">انقر على الخريطة لاختيار موقع العيادة</small>
                                    </div>
                                    <div style={{ height: "300px", border: "2px solid #dee2e6", borderRadius: "8px", overflow: "hidden" }}>
                                        {(clinicLocation || currentLocation) ? (
                                            <OpenStreetMapComponent
                                                key={mapKey}
                                                center={clinicLocation ? { lat: clinicLocation.latitude, lng: clinicLocation.longitude } : { lat: currentLocation.latitude, lng: currentLocation.longitude }}
                                                clinicLocation={clinicLocation}
                                                onMapClick={(loc) => {
                                                    setClinicLocation(loc);
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        clinic_latitude: String(loc.latitude),
                                                        clinic_longitude: String(loc.longitude),
                                                    }));
                                                    setMapKey((prev) => prev + 1);
                                                }}
                                            />
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center h-100 bg-light">
                                                <div className="text-center text-muted">
                                                    <i className="fas fa-map-marker-alt fa-3x mb-3"></i>
                                                    <p>لم يتم تحديد موقع العيادة بعد</p>
                                                    <p>استخدم زر "استخدام الموقع الحالي" أو انقر على الخريطة لتحديد الموقع</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="row g-3 mt-2">
                                        <div className="col-md-6">
                                            <label className="form-label">خط العرض (Latitude)</label>
                                            <input
                                                type="number"
                                                step="any"
                                                name="clinic_latitude"
                                                value={formData.clinic_latitude || ""}
                                                onChange={handleChange}
                                                className="form-control"
                                                dir="ltr"
                                                placeholder="مثال: 33.3152"
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">خط الطول (Longitude)</label>
                                            <input
                                                type="number"
                                                step="any"
                                                name="clinic_longitude"
                                                value={formData.clinic_longitude || ""}
                                                onChange={handleChange}
                                                className="form-control"
                                                dir="ltr"
                                                placeholder="مثال: 44.3661"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2 text-end">
                                        <small className="text-muted">
                                            {formData.clinic_latitude && formData.clinic_longitude
                                                ? `الموقع الحالي: ${Number(formData.clinic_latitude).toFixed(6)}, ${Number(formData.clinic_longitude).toFixed(6)}`
                                                : "لم يتم تحديد موقع"}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Open Days */}
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-light fw-semibold">أيام الدوام</div>
                            <div className="card-body">
                                {/* Day chips selection */}
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                                        <label className="form-label mb-2">أيام دوام العيادة</label>
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-light btn-sm"
                                                onClick={() => {
                                                    const allDays = daysOfWeek.map(d => d.english);
                                                    const times = {};
                                                    allDays.forEach(d => {
                                                        times[d] = { opening_time: "", closing_time: "" };
                                                    });
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        open_days: allDays,
                                                        open_day_times: times,
                                                    }));
                                                }}
                                            >
                                                تحديد الكل
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => setFormData(prev => ({ ...prev, open_days: [], open_day_times: {} }))}
                                            >
                                                مسح
                                            </button>
                                        </div>
                                    </div>
                                    <div className="open-days-group text-end">
                                        {daysOfWeek.map(({ english, arabic }) => {
                                            const isSelected = formData.open_days.includes(english);
                                            return (
                                                <span key={english} className="me-1 mb-2 d-inline-block">
                                                    <input
                                                        type="checkbox"
                                                        className="btn-check"
                                                        id={`open_day_${english}`}
                                                        autoComplete="off"
                                                        checked={isSelected}
                                                        onChange={() => {
                                                            // mimic toggleOpenDay from DoctorInformation
                                                            setFormData(prev => {
                                                                const exists = prev.open_days.includes(english);
                                                                const nextDays = exists
                                                                    ? prev.open_days.filter(d => d !== english)
                                                                    : [...prev.open_days, english];
                                                                const nextTimes = { ...(prev.open_day_times || {}) };
                                                                if (!exists) {
                                                                    if (!nextTimes[english]) {
                                                                        nextTimes[english] = { opening_time: "", closing_time: "" };
                                                                    }
                                                                } else {
                                                                    delete nextTimes[english];
                                                                }
                                                                return { ...prev, open_days: nextDays, open_day_times: nextTimes };
                                                            });
                                                        }}
                                                    />
                                                    <label className="btn btn-outline-primary btn-sm rounded-pill" htmlFor={`open_day_${english}`}>
                                                        {arabic}
                                                    </label>
                                                </span>
                                            );
                                        })}
                                    </div>
                                    <small className="text-muted d-block text-end mt-1">حدد الأيام التي تعمل فيها العيادة.</small>
                                </div>

                                {/* Times for each selected day */}
                                {formData.open_days.length > 0 && (
                                    <div className="mb-0">
                                        <label className="form-label text-end d-block">أوقات الدوام لكل يوم</label>
                                        <div className="row g-2">
                                            {formData.open_days.map((dayName) => {
                                                const times = formData.open_day_times?.[dayName] || { opening_time: "", closing_time: "" };
                                                const arabicLabel = (daysOfWeek.find(d => d.english === dayName) || {}).arabic || dayName;
                                                return (
                                                    <div className="col-12" key={`times-${dayName}`}>
                                                        <div className="p-2 border rounded d-flex flex-wrap align-items-center justify-content-between" dir="rtl">
                                                            <strong className="mb-2 mb-sm-0">{arabicLabel}</strong>
                                                            <div className="d-flex gap-2 align-items-center flex-row" dir="ltr">
                                                                                                                                {/* To (closing) second */}
                                                                <div className="input-group input-group-sm">
                                                                    <span className="input-group-text"><i className="far fa-clock"></i></span>
                                                                    <select
                                                                        className="form-select"
                                                                        value={times.closing_time || ""}
                                                                        onChange={(e) => handleTimeChange(dayName, 'closing_time', e.target.value)}
                                                                    >
                                                                        <option value="">— إلى —</option>
                                                                        {HOUR_OPTIONS.map((h) => (
                                                                            <option key={`to-${dayName}-${h}`} value={h}>{h}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                                {/* From (opening) should appear first visually */}
                                                                <div className="input-group input-group-sm">
                                                                    <span className="input-group-text"><i className="far fa-clock"></i></span>
                                                                    <select
                                                                        className="form-select"
                                                                        value={times.opening_time || ""}
                                                                        onChange={(e) => handleTimeChange(dayName, 'opening_time', e.target.value)}
                                                                    >
                                                                        <option value="">— من —</option>
                                                                        {HOUR_OPTIONS.map((h) => (
                                                                            <option key={`from-${dayName}-${h}`} value={h}>{h}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Social & Media */}
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-light fw-semibold">الروابط والصورة</div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">رابط الفيسبوك</label>
                                        <input type="url" name="facebook_url" value={formData.facebook_url || ""} dir="ltr" onChange={handleChange} className="form-control" placeholder="https://facebook.com/username" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">رابط الانستغرام</label>
                                        <input type="url" name="instagram_url" value={formData.instagram_url || ""} dir="ltr" onChange={handleChange} className="form-control" placeholder="https://instagram.com/username" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">الصورة الشخصية</label>
                                        <input type="file" name="profile_picture" accept="image/*" onChange={handleChange} className="form-control" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-grid">
                            <button type="submit" className="btn btn-primary">إضافة</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
